/**
 * A button for an action a slip cannot undo: pressing and holding it for two
 * seconds fills it from the left, and releasing early drains the fill and does
 * nothing. Pointer and keyboard (Enter or Space) both hold.
 *
 * Assistive technology activates a button with a single synthesized click and
 * cannot hold, so a click that no press on this button started confirms
 * straight away; the visible label ("Hold to …") already names the action.
 *
 * The fill is the only sign of how long is left, so it keeps its motion under
 * reduced motion (`data-motion="essential"`). The timer, not the transition,
 * decides when the hold completes.
 */
import {
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const HOLD_TO_CONFIRM_MS = 2000;

type HoldToConfirmButtonProps = Omit<
  ComponentProps<typeof Button>,
  | 'asChild'
  | 'onClick'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onPointerCancel'
  | 'onPointerDown'
  | 'onPointerLeave'
  | 'onPointerUp'
> & {
  readonly onConfirm: () => void;
};

function isHoldKey(event: KeyboardEvent<HTMLButtonElement>): boolean {
  return event.key === 'Enter' || event.key === ' ';
}

export function HoldToConfirmButton({
  onConfirm,
  disabled = false,
  className,
  children,
  ...props
}: HoldToConfirmButtonProps): ReactNode {
  const [holding, setHolding] = useState(false);
  // Set by a pointer press so the click that follows its release is ignored.
  const pressed = useRef(false);
  const confirm = useEffectEvent(onConfirm);

  // A button disabled mid-hold never sees the release, so drop the hold here
  // rather than let it complete or resume when the button comes back.
  if (disabled && holding) {
    setHolding(false);
  }

  useEffect(() => {
    if (!holding) return;
    const timer = window.setTimeout(() => {
      setHolding(false);
      confirm();
    }, HOLD_TO_CONFIRM_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [holding]);

  return (
    <Button
      {...props}
      disabled={disabled}
      data-holding={holding ? 'true' : undefined}
      // `isolate` lets the fill sit between the button's own background and its label; `touch-none` keeps a drifting finger from turning the hold into a scroll.
      className={cn('relative isolate overflow-hidden touch-none', className)}
      onPointerDown={(event: PointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        pressed.current = true;
        setHolding(true);
      }}
      onPointerUp={() => {
        setHolding(false);
      }}
      onPointerLeave={() => {
        setHolding(false);
      }}
      onPointerCancel={() => {
        setHolding(false);
      }}
      onKeyDown={(event) => {
        if (!isHoldKey(event)) return;
        // The native activation would confirm on a tap; the hold owns these keys.
        event.preventDefault();
        if (!event.repeat) setHolding(true);
      }}
      onKeyUp={(event) => {
        if (!isHoldKey(event)) return;
        event.preventDefault();
        setHolding(false);
      }}
      onClick={() => {
        if (pressed.current) {
          pressed.current = false;
          return;
        }
        onConfirm();
      }}
    >
      <span
        aria-hidden="true"
        data-motion="essential"
        className="pointer-events-none absolute inset-0 -z-10 bg-current/15 transition-[clip-path] duration-200 ease-out-quart [clip-path:inset(0_100%_0_0)] group-data-[holding=true]/button:duration-2000 group-data-[holding=true]/button:ease-linear group-data-[holding=true]/button:[clip-path:inset(0)]"
      />
      {children}
    </Button>
  );
}
